<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeTooltip from "@dsk/components/LeTooltip.vue";
import LeLock from "@dsk/icons/LeLock.vue";
import LeView from "@dsk/icons/LeView.vue";
import { useI18n } from "vue-i18n";

import { MatomoHelper } from "@/helpers";
import { useSearchStore } from "@/stores";
import type { Dictionary, SearchViewType } from "@/types";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

import LeUpsellAccessRequestModalStateButton from "./LeUpsellAccessRequestModalStateButton.vue";

const props = defineProps<{
  searchViewType: SearchViewType;
  unlockable: boolean;
  dictionary: Dictionary;
}>();

const searchStore = useSearchStore();
const { t } = useI18n();

const onUnlock = () => {
  searchStore.upsellUnlock(props.dictionary);

  MatomoHelper.sendEvent(
    MatomoEventCategory.DictionaryUpsell,
    MatomoEventAction.ClickShowContent,
    `Clicked show content on dictionary upselling article [${props.searchViewType}]`
  );
};
</script>

<template>
  <div class="le-upsell-overlay">
    <template v-if="props.searchViewType === 'classic'">
      <div class="le-upsell-overlay__classic-box">
        <p class="le-upsell-overlay__narrow le-upsell-overlay__text">
          <i18n-t tag="span" class="le-upsell-overlay__info-text" keypath="le-upsell.classic.text.mobile">
            <template #dictionary>
              <span class="le-upsell-overlay__emphasize">{{ props.dictionary.title }}</span>
            </template>
          </i18n-t>
        </p>

        <LeHeading tag="h4" class="le-upsell-overlay__wide le-upsell-overlay__header">{{
          t("le-upsell.classic.title")
        }}</LeHeading>
        <p class="le-upsell-overlay__wide le-upsell-overlay__text">
          <i18n-t
            v-if="props.unlockable"
            tag="span"
            class="le-upsell-overlay__info-text"
            keypath="le-upsell.classic.text.desktop"
          >
            <template #dictionary>
              <span class="le-upsell-overlay__emphasize">{{ props.dictionary.title }}</span>
            </template>
          </i18n-t>
          <i18n-t v-else tag="span" class="le-upsell-overlay__info-text" keypath="le-upsell.classic.no-more.text">
            <template #dictionary>
              <span class="le-upsell-overlay__emphasize">{{ props.dictionary.title }}</span>
            </template>
          </i18n-t>
        </p>

        <div class="le-upsell-overlay__buttons">
          <LeUpsellAccessRequestModalStateButton
            :dictionary="props.dictionary"
            :search-view-type="props.searchViewType"
            tracking-context="overlay"
            type="button"
            color="attention"
          >
            <LeLock role="presentation" /> {{ t("le-upsell.classic.button.get-access") }}
          </LeUpsellAccessRequestModalStateButton>
          <LeButton v-if="props.unlockable" color="attention" variant="outline" @click="onUnlock()">
            <LeView role="presentation" /> {{ t("le-upsell.classic.button.show-preview") }}
          </LeButton>
        </div>
      </div>
    </template>

    <template v-else-if="props.searchViewType === 'compact'">
      <div class="le-upsell-overlay__narrow">
        <div class="le-upsell-overlay__compact-box-wrapper">
          <div class="le-upsell-overlay__compact-box">
            <LeHeading tag="h4" class="le-upsell-overlay__header">{{ t("le-upsell.compact.title.mobile") }}</LeHeading>

            <div class="le-upsell-overlay__body-icons-wrapper">
              <div class="le-upsell-overlay__body-icons">
                <LeUpsellAccessRequestModalStateButton
                  :dictionary="props.dictionary"
                  :search-view-type="props.searchViewType"
                  tracking-context="overlay"
                  type="icon-button"
                  color="accent"
                >
                  <LeLock role="presentation" />
                </LeUpsellAccessRequestModalStateButton>
                <LeUpsellAccessRequestModalStateButton
                  :dictionary="props.dictionary"
                  :search-view-type="props.searchViewType"
                  tracking-context="overlay"
                  type="text-button"
                  class="le-upsell-overlay__body-icons-text"
                  tabindex="-1"
                >
                  {{ t("le-upsell.compact.link.get-access") }}
                </LeUpsellAccessRequestModalStateButton>
              </div>
              <div v-if="props.unlockable" class="le-upsell-overlay__body-icons" @click="onUnlock()">
                <LeIconButton color="accent"><LeView role="presentation" /></LeIconButton>
                <span class="le-upsell-overlay__body-icons-text">{{ t("le-upsell.compact.link.show-preview") }}</span>
              </div>
            </div>
          </div>
          <div class="le-upsell-overlay__compact-box-footer">{{ props.dictionary.title }}</div>
        </div>
      </div>

      <div class="le-upsell-overlay__wide">
        <div class="le-upsell-overlay__compact-box">
          <div class="le-upsell-overlay__header-wrapper">
            <LeHeading tag="h4" class="le-upsell-overlay__header">{{ t("le-upsell.compact.title.desktop") }}</LeHeading>

            <div class="le-upsell-overlay__header-icons">
              <LeTooltip :text="t('le-upsell.compact.tooltip.get-access')">
                <LeUpsellAccessRequestModalStateButton
                  :dictionary="props.dictionary"
                  :search-view-type="props.searchViewType"
                  tracking-context="overlay"
                  type="icon-button"
                  color="accent"
                >
                  <LeLock role="presentation" />
                </LeUpsellAccessRequestModalStateButton>
              </LeTooltip>
              <LeTooltip v-if="props.unlockable" :text="t('le-upsell.compact.tooltip.show-preview')">
                <LeIconButton color="accent" @click="onUnlock()"><LeView role="presentation" /></LeIconButton>
              </LeTooltip>
            </div>
          </div>

          <p class="le-upsell-overlay__text">
            <i18n-t
              v-if="props.unlockable"
              tag="span"
              class="le-upsell-overlay__info-text"
              keypath="le-upsell.compact.text"
            >
              <template #dictionary>
                <span class="le-upsell-overlay__emphasize">{{ props.dictionary.title }}</span>
              </template>
            </i18n-t>
            <i18n-t v-else tag="span" class="le-upsell-overlay__info-text" keypath="le-upsell.compact.no-more.text">
              <template #dictionary>
                <span class="le-upsell-overlay__emphasize">{{ props.dictionary.title }}</span>
              </template>
            </i18n-t>
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="less">
@import "@dsk/styles/all";

.le-upsell-overlay {
  &__narrow {
    display: flex;

    #media-breakpoint-up(sm, {
      display: none;
    });
  }

  &__wide {
    display: none;

    #media-breakpoint-up(sm, {
      display: flex;
    });
  }

  &__classic-box {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px;
    border-radius: 4px 4px 0px 0px;
    background: @colors[@accent][200];
    box-shadow: @shadows[floating-default];
    text-align: center;

    #media-breakpoint-up(sm, {
      text-align: left;
    });
  }

  &__compact-box {
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    padding: 12px 20px;
    border-radius: 4px 4px 0px 0px;
    background: @colors[@accent][200];
    box-shadow: @shadows[floating-default];
    text-align: center;

    #media-breakpoint-up(sm, {
      gap: 3px;
      text-align: left;
    });

    &-wrapper {
      width: 100%;
    }

    &-footer {
      justify-content: center;
      padding: 4px 20px;
      background: @colors[@accent][300];
      box-shadow: @shadows[floating-default];
      text-align: center;
      .body-text("xs", "normal", "italic");
    }
  }

  &__header-wrapper {
    display: flex;
    align-items: center;
    align-self: stretch;
    justify-content: space-between;
  }

  &__header-icons {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
  }

  &__header {
    color: @colors[@accent][900];
  }

  &__text {
    margin-bottom: 0;
    .body-text("s");
    color: @colors[@accent][900];
    white-space: pre-line;
  }

  &__emphasize {
    .body-text("s", "semi-bold");
  }

  &__body-icons-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-around;
    gap: 5px;
  }

  &__body-icons {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  :deep(&__body-icons-text) {
    .body-text("s");
    text-decoration: underline;
    cursor: pointer;
  }

  &__buttons {
    display: flex;
    gap: 12px;
    flex-direction: column;
    align-items: center;
    margin-top: 6px;

    #media-breakpoint-up(sm, {
      flex-direction: row;
      align-items: flex-start;
    });
  }
}
</style>
